import { ICurrency } from "adminPanel/pos/models/IPosProperties";
import { NIL, v4 as uuid4 } from "uuid";
import i18n from "localization/i18n";
import IBankCard from "adminPanel/settings/bankCards/IBankCard";
import { IGCCClientTax } from "adminPanel/settings/settingTaxes/ITaxSystem";

export interface IClient {
  id: string | null;
  name: string | null;
  lastModified: string;
  gccTax?: IGCCClientTax;
}

export interface TicketPaymentGetway {
  id: string;
  ticketId: string;
  currencyId: string;
  currencyVal: number;
  payType: PaymentMethodType;
  amount: number;
  bankcardId: string;
  verificationCode?: string;
}

export enum PaymentMethodType {
  Currency,
  Dept,
  BankCard,
  CashDiscount,
}

export type DebitPaymentMethod = {
  id: string;
  icon: string;
  iso: string;
  localizedName: string;
  type: PaymentMethodType.Dept;
  paymentValue: number;
};

export type CashDiscountPaymentMethod = {
  id: string;
  icon: string;
  iso: string;
  localizedName: string;
  type: PaymentMethodType.CashDiscount;
  paymentValue: number;
};

export type CurrencyPaymentMethod = {
  id: string;
  icon: string;
  code: string;
  iso: string;
  isDefault: boolean;
  localizedName: string;
  type: PaymentMethodType.Currency;
  paymentValue: number;
  equivalentPaymentValue: number;
  currencyValue: number;
  currencyId: string;
};

export type BankCardPaymentMethod = {
  id: string;
  icon: string;
  iso: string;
  localizedName: string;
  type: PaymentMethodType.BankCard;
  paymentValue: number;
  bankCardId: string;
  minimumLimit: number;
  enterVerificationNumber: boolean;
  LocalizedAbbrevName: string;
  verificationCode: string;
};

export const EmptyBankCardPaymentMethod: BankCardPaymentMethod = {
  id: "",
  icon: "",
  iso: "",
  localizedName: "",
  type: PaymentMethodType.BankCard,
  paymentValue: 0,
  bankCardId: "",
  minimumLimit: 0,
  enterVerificationNumber: true,
  LocalizedAbbrevName: "",
  verificationCode: "",
};

export type PaymentItem =
  | DebitPaymentMethod
  | CurrencyPaymentMethod
  | BankCardPaymentMethod
  | CashDiscountPaymentMethod;

export type PaymentDetails = {
  valueToPaid: number;
  totalPaid: number;
  oldTotalPaymentsPaid: number;
  totalRemain: number;
  payments: PaymentItem[];
};
export const EmptyPaymentItem: PaymentItem = {
  id: NIL,
  localizedName: "",
  icon: "",
  code: "",
  iso: "",
  isDefault: false,
  type: PaymentMethodType.Currency,
  paymentValue: 0,
  equivalentPaymentValue: 0,
  currencyValue: 0,
  currencyId: NIL,
};

export const InitBankCardPaymentMethod = (bankcard: IBankCard) => {
  return {
    id: uuid4(),
    localizedName: bankcard.name,
    icon: bankcard.image,
    iso: bankcard.shortName,
    LocalizedAbbrevName: bankcard.shortName,
    type: PaymentMethodType.BankCard,
    paymentValue: 0,
    minimumLimit: bankcard.minimumLimit,
    bankCardId: bankcard.id,
    enterVerificationNumber: bankcard.enterVerificationNumber,
  } as BankCardPaymentMethod;
};

export const InitCurrencyPaymentMethod = (currency: ICurrency) => {
  return {
    id: uuid4(),
    localizedName: i18n.language === "ar" ? currency.name_Ar : currency.name,
    icon: currency.flagCode,
    code: currency.code,
    iso: currency.iso,
    isDefault: currency.isDefault,
    type: PaymentMethodType.Currency,
    paymentValue: 0,
    equivalentPaymentValue: 0,
    currencyValue: currency.exchangeFactor,
    currencyId: currency.id,
  } as CurrencyPaymentMethod;
};

export const InitDebtPaymentMethod = (icon: string, localizedName: string) => {
  return {
    id: uuid4(),
    icon,
    localizedName,
    paymentValue: 0,
    type: PaymentMethodType.Dept,
  } as DebitPaymentMethod;
};

export const InitCashDiscountPaymentMethod = (
  icon: string,
  localizedName: string
) => {
  return {
    id: uuid4(),
    icon,
    localizedName,
    paymentValue: 0,
    type: PaymentMethodType.CashDiscount,
  } as CashDiscountPaymentMethod;
};

export const EmptyClient: IClient = {
  id: NIL,
  name: "",
  lastModified:"",
};

export const EmptyTicketPaymentGetway: TicketPaymentGetway = {
  id: NIL,
  ticketId: NIL,
  currencyId: NIL,
  currencyVal: 1,
  payType: PaymentMethodType.Currency,
  amount: 0,
  bankcardId: NIL,
};
